/**
 * La scheda di punteggio **bersaglio per bersaglio**, sul modello PractiScore.
 *
 * È la forma che il riferimento del settore usa da sempre, ed è anche quella
 * del regolamento: una riga per bersaglio (T1, T2, …), una cella per zona, si
 * **tocca per incrementare**. Due Alpha su T1 sono due tocchi su A — nessuna
 * tastiera numerica, che al poligono col telefono è la differenza fra
 * registrare e rimandare.
 *
 * Quattro comportamenti presi dal riferimento, ognuno per un motivo:
 *
 *  - **tocco sull'etichetta T1 azzera la riga** — correggere senza cancellare
 *    carattere per carattere;
 *  - **riga verde quando il bersaglio è completo, rossa quando ha troppi
 *    impatti** — è il punto 26, risolto *mostrandolo*. Il cap viene dal
 *    regolamento: due migliori impatti sulla carta (FITDS 9.5.1), massimo due
 *    penalità no-shoot per bersaglio (9.4.2 e 9.4.3);
 *  - **il metallo si abbatte, non si legge a zone** — un popper o un piatto
 *    non hanno A/C/D: cadono o restano in piedi. La riga di un ferro ha una
 *    cella sola, «Abbattuto», che vale la zona migliore del sistema (il
 *    metallo abbattuto vale A); il colpo che non lo abbatte è un Miss;
 *  - **niente blocchi duri.** Un impatto in più si segnala, non si impedisce:
 *    un dato strano va mostrato, o al poligono si perde la registrazione.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo D
 */
import { Check } from "lucide-react";
import {
	defaultHitsRequired,
	elementSpec,
	isSteelTarget,
	targetPrefix,
} from "./stageElements";

/** Massimo di penalità no-shoot per bersaglio (FITDS 9.4.2 e 9.4.3). */
const MAX_NO_SHOOT_PENALTIES = 2;

const sum = (counts) =>
	Object.values(counts || {}).reduce((total, n) => total + (Number(n) || 0), 0);

/**
 * I bersagli da scorare: solo quelli che si sparano, già numerati.
 *
 * I no-shoot restano fuori dalla scheda — il tiratore non deve cercare un T4
 * che non deve compilare — ma le loro penalità si registrano sul bersaglio
 * che si stava ingaggiando, che è come funziona davvero.
 */
export const scorableTargets = (targets) => {
	// Il numero è l'identità con cui la scheda riaggancia l'impatto al
	// bersaglio: quello salvato vince, gli altri si assegnano in lettura. Un
	// gruppo `3× IPSC` (righe scritte prima che ogni sagoma fosse una riga) è
	// tre bersagli e **consuma tre numeri**: contarlo come uno dava al
	// bersaglio successivo lo stesso numero del secondo della terna.
	let next = 0;
	const list = [];
	for (const target of targets || []) {
		if (defaultHitsRequired(target.target_type) === 0) continue;
		const count = Math.max(1, Number(target.count) || 1);
		const first = target.target_no ?? next + 1;
		next += count;
		for (let i = 0; i < count; i += 1) {
			list.push({ ...target, count: 1, target_no: first + i });
		}
	}
	return list;
};

/**
 * Il nome di ogni bersaglio da scorare, per numero: `T1`, `T2`, `P1`, `S1`.
 *
 * La lettera dice il tipo (`targetPrefix`), il numero conta **dentro la
 * lettera**: tre carte e due popper sono T1-T3 e P1-P2, come sulla piantina
 * di gara. Il `target_no` resta l'identità salvata sul risultato — qui si
 * decide solo come chiamarlo, così i risultati già registrati non cambiano.
 */
export const targetLabels = (targets) => {
	const list = [...scorableTargets(targets)].sort(
		(a, b) => a.target_no - b.target_no,
	);
	const counters = {};
	const labels = new Map();
	for (const target of list) {
		if (labels.has(target.target_no)) continue;
		const prefix = targetPrefix(target.target_type);
		counters[prefix] = (counters[prefix] || 0) + 1;
		labels.set(target.target_no, `${prefix}${counters[prefix]}`);
	}
	return labels;
};

/** Il nome di un bersaglio dal suo numero, con `T` dove non si sa il tipo. */
export const targetLabelOf = (labels, targetNo) =>
	labels?.get(targetNo) || `T${targetNo}`;

/**
 * Il prossimo valore della cella «Abbattuto» di un ferro.
 *
 * Si tocca finché il ferro non è giù (un popper vuole un colpo, un plate rack
 * ne vuole sei), e il tocco successivo **torna a zero**: su un bersaglio che
 * cade o non cade «troppi impatti» non esiste, ed è più rapido ripartire che
 * cercare il tocco lungo per togliere.
 */
export const nextSteelHits = (count, required) => {
	const current = Math.max(0, Number(count) || 0);
	const needed = Math.max(1, Number(required) || 1);
	return current >= needed ? 0 : current + 1;
};

export function TargetScoreCard({
	targets,
	zones,
	penalties,
	value = [],
	onChange,
	courseType = "comstock",
}) {
	const list = scorableTargets(targets);
	const labels = targetLabels(targets);
	const byNo = new Map(value.map((row) => [row.target_no, row]));
	// Il metallo abbattuto vale la zona migliore: è la prima del sistema, che
	// si scrive dalla migliore alla peggiore (A, C, D), come fa il backend.
	const steelZone = zones[0]?.code;
	const columns = `2.2rem repeat(${zones.length + penalties.length}, minmax(0, 1fr))`;

	const rowFor = (no) => byNo.get(no) || { target_no: no, hits: {}, penalties: {} };

	const write = (no, next) => {
		const others = value.filter((row) => row.target_no !== no);
		const isEmpty = sum(next.hits) === 0 && sum(next.penalties) === 0;
		onChange(isEmpty ? others : [...others, next]);
	};

	const bump = (no, group, code, delta) => {
		const row = rowFor(no);
		const counts = { ...(row[group] || {}) };
		const next = Math.max(0, (counts[code] || 0) + delta);
		if (next === 0) delete counts[code];
		else counts[code] = next;
		write(no, { ...row, [group]: counts });
	};

	// Un ferro scrive i colpi su una zona sola: se un risultato vecchio li ha
	// sparsi su C o D, il primo tocco li riporta lì.
	const setSteelHits = (no, count) => {
		const row = rowFor(no);
		write(no, {
			...row,
			hits: count > 0 && steelZone ? { [steelZone]: count } : {},
		});
	};

	const clearRow = (no) => write(no, { target_no: no, hits: {}, penalties: {} });

	if (list.length === 0) return null;

	const penaltyCells = (target, label, row) =>
		penalties.map((penalty) => {
			const count = row.penalties?.[penalty.code] || 0;
			return (
				<button
					key={penalty.code}
					type="button"
					onClick={() => bump(target.target_no, "penalties", penalty.code, 1)}
					onContextMenu={(e) => {
						e.preventDefault();
						bump(target.target_no, "penalties", penalty.code, -1);
					}}
					className={`h-10 rounded-sm text-base font-medium tabular-nums transition-colors ${
						count > 0
							? "bg-red-500/20 text-red-400"
							: "bg-background text-muted-foreground/40 hover:bg-background/60"
					}`}
					aria-label={`${label} penalità ${penalty.code}: ${count}`}
				>
					{count || "·"}
				</button>
			);
		});

	return (
		<div className="space-y-1">
			{/* Intestazioni: le celle sono quadrati numerici, e senza il codice
			    sopra non si sa quale sia A e quale D. */}
			<div className="grid gap-1 px-1" style={{ gridTemplateColumns: columns }}>
				<span />
				{zones.map((zone) => (
					<span
						key={zone.code}
						className="text-[10px] text-center text-muted-foreground"
					>
						{zone.code}
					</span>
				))}
				{penalties.map((penalty) => (
					<span
						key={penalty.code}
						className="text-[10px] text-center text-red-400/80"
					>
						{penalty.code}
					</span>
				))}
			</div>

			{list.map((target) => {
				const spec = elementSpec(target.target_type);
				const row = rowFor(target.target_no);
				const label = targetLabelOf(labels, target.target_no);
				const required =
					target.hits_required ?? defaultHitsRequired(target.target_type);
				const scored = sum(row.hits);
				const isSteel = isSteelTarget(target.target_type);
				// Comstock ammette colpi liberi, ma gli **impatti che contano**
				// restano quelli: oltre, la riga è rossa.
				const tooMany =
					scored > required ||
					sum(row.penalties) > MAX_NO_SHOOT_PENALTIES * 2;
				const complete = scored === required && !tooMany;

				return (
					<div
						key={target.target_no}
						className={`grid gap-1 items-center rounded-sm border px-1 py-1 transition-colors ${
							tooMany
								? "border-red-500/60 bg-red-500/10"
								: complete
									? "border-volt/50 bg-volt/10"
									: "border-border/60 bg-secondary/30"
						}`}
						style={{ gridTemplateColumns: columns }}
					>
						<button
							type="button"
							onClick={() => clearRow(target.target_no)}
							title={`${spec.label} — tocca per azzerare`}
							className="h-10 rounded-sm text-[11px] font-semibold text-muted-foreground hover:text-red-400 flex flex-col items-center justify-center leading-none"
						>
							<span>{label}</span>
							<span className="text-[9px] opacity-60 font-normal">
								{isSteel ? "ferro" : `×${required}`}
							</span>
						</button>

						{isSteel ? (
							/* Il ferro non ha zone: una cella sola che copre le
							   colonne A/C/D. Colpito e abbattuto, oppure no — e il
							   colpo che non lo abbatte si segna in M. */
							<button
								type="button"
								onClick={() =>
									setSteelHits(target.target_no, nextSteelHits(scored, required))
								}
								onContextMenu={(e) => {
									e.preventDefault();
									setSteelHits(target.target_no, Math.max(0, scored - 1));
								}}
								style={{ gridColumn: `span ${Math.max(1, zones.length)}` }}
								className={`h-10 min-w-0 px-1 rounded-sm text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
									scored > 0
										? "bg-volt/20 text-volt"
										: "bg-background text-muted-foreground/70 hover:bg-background/60"
								}`}
								aria-label={`${label} abbattuto: ${scored} di ${required}`}
							>
								{scored > 0 && <Check className="w-4 h-4 shrink-0" />}
								<span className="truncate">
									{scored > 0 ? "Abbattuto" : "Abbattuto?"}
								</span>
								{required > 1 && (
									<span className="tabular-nums text-xs opacity-80">
										{scored}/{required}
									</span>
								)}
							</button>
						) : (
							zones.map((zone) => {
								const count = row.hits?.[zone.code] || 0;
								return (
									<button
										key={zone.code}
										type="button"
										onClick={() => bump(target.target_no, "hits", zone.code, 1)}
										onContextMenu={(e) => {
											e.preventDefault();
											bump(target.target_no, "hits", zone.code, -1);
										}}
										className={`h-10 rounded-sm text-base font-medium tabular-nums transition-colors ${
											count > 0
												? "bg-volt/20 text-volt"
												: "bg-background text-muted-foreground/40 hover:bg-background/60"
										}`}
										aria-label={`${label} zona ${zone.code}: ${count}`}
									>
										{count || "·"}
									</button>
								);
							})
						)}

						{penaltyCells(target, label, row)}
					</div>
				);
			})}
			{/* La legenda delle lettere, solo se la scheda ne usa più di una:
			    su uno stage di sole carte non c'è niente da spiegare. */}
			{new Set(list.map((target) => targetPrefix(target.target_type))).size > 1 && (
				<p className="text-[10px] text-muted-foreground px-1">
					T carta · P popper · S piatto — sul ferro conta solo se è caduto
				</p>
			)}
		</div>
	);
}

/**
 * Stato della scheda: quanti bersagli sono completi, e se è pronta.
 *
 * È il campo "Ready" del riferimento — dice se si può salvare prima di
 * scoprirlo dopo. Puro, così è testabile.
 */
export const targetCardStatus = (targets, rows = []) => {
	const list = scorableTargets(targets);
	if (list.length === 0) return null;
	const byNo = new Map(rows.map((row) => [row.target_no, row]));
	let done = 0;
	let over = 0;
	for (const target of list) {
		const row = byNo.get(target.target_no);
		const scored = sum(row?.hits);
		const required =
			target.hits_required ?? defaultHitsRequired(target.target_type);
		if (scored > required) over += 1;
		else if (scored === required) done += 1;
	}
	return { total: list.length, done, over, ready: done === list.length };
};

export function TargetCardStatusLine({ status }) {
	if (!status) return null;
	if (status.over > 0) {
		return (
			<p className="text-[11px] text-red-400">
				{status.over} bersagli con più impatti di quanti ne contino.
			</p>
		);
	}
	return (
		<p
			className={`text-[11px] flex items-center gap-1 ${
				status.ready ? "text-volt" : "text-muted-foreground"
			}`}
		>
			{status.ready && <Check className="w-3 h-3" />}
			{status.ready
				? "Scheda completa"
				: `${status.done} di ${status.total} bersagli scorati`}
		</p>
	);
}
